import React from "react";
import { useTenant } from "../context/TenantContext";
import "../styles/returns.css";

const ReturnsPolicy = () => {
  const { tenant } = useTenant();

  // Helper function to format phone number for tel: links
  const formatTelLink = (phone) => {
    if (!phone) return "";
    return phone.replace(/[\s\-\(\)]/g, "");
  };

  // Get returns policy content from tenant
  const returnsContent = tenant?.ReturnsContent;

  // Check if we have custom content from the admin
  const hasCustomContent = returnsContent && returnsContent.trim() !== "";

  // If custom content exists, render it directly
  if (hasCustomContent) {
    return (
      <div className="returns-page">
        <div className="container">
          <section className="returns-hero">
            <h1>Returns & Exchanges Policy</h1>
            <p>Our commitment to your satisfaction</p>
          </section>
          <div
            className="returns-content"
            dangerouslySetInnerHTML={{ __html: returnsContent }}
          />
        </div>
      </div>
    );
  }

  // Default content using tenant data from backend
  return (
    <div className="returns-page">
      <div className="container">
        <section className="returns-hero">
          <h1>Returns & Exchanges Policy</h1>
          <p>Our commitment to your satisfaction</p>
        </section>

        <div className="returns-content">
          <div className="returns-section">
            <h2>Returns Policy</h2>
            <p>
              At {tenant?.BusinessName || "our company"}, we want you to be
              completely satisfied with your purchase. If you are not happy with
              your order, we're here to help.
            </p>
          </div>

          <div className="returns-section">
            <h3>Return Window</h3>
            <p>
              You have <strong>30 days</strong> from the date of delivery to
              return your item for a full refund or exchange. Items must be
              returned in their original condition, unused, and with all
              original packaging and tags attached.
            </p>
          </div>

          <div className="returns-section">
            <h3>Eligibility Criteria</h3>
            <p>To be eligible for a return, items must:</p>
            <ul>
              <li>Be in original, unused condition</li>
              <li>Have all original packaging and tags</li>
              <li>Not be damaged or show signs of wear</li>
              <li>Be returned within the 30-day window</li>
              <li>
                Not be on the list of non-returnable items (see exceptions
                below)
              </li>
            </ul>
          </div>

          <div className="returns-section">
            <h3>Return Process</h3>
            <p>To initiate a return, please follow these steps:</p>
            <ol>
              <li>
                Log in to your account and go to your order history, or contact
                our support team.
              </li>
              <li>
                Request a return authorization and receive a Return Merchandise
                Authorization (RMA) number.
              </li>
              <li>
                Package the item securely and include the RMA number on the
                outside of the package.
              </li>
              <li>
                Ship the item to our returns address using a trackable shipping
                method.
              </li>
              <li>
                Once we receive and inspect the item, we will process your
                refund or exchange within <strong>5-7 business days</strong>.
              </li>
            </ol>
          </div>

          <div className="returns-section">
            <h3>Refund Information</h3>
            <p>
              Refunds will be issued to the original payment method used for the
              purchase. Please allow <strong>5-10 business days</strong> for the
              refund to appear in your account, depending on your bank or
              payment provider.
            </p>
            <p>
              <strong>Note:</strong> Original shipping charges are
              non-refundable unless the return is due to an error on our part.
            </p>
          </div>

          <div className="returns-section">
            <h3>Exchanges</h3>
            <p>
              If you would like to exchange an item for a different size, color,
              or product, please follow the same return process and indicate
              your exchange request. We will process the exchange once we
              receive your returned item.
            </p>
          </div>

          <div className="returns-section">
            <h3>Return Shipping Costs</h3>
            <p>
              Customers are responsible for return shipping costs unless the
              return is due to:
            </p>
            <ul>
              <li>Damaged or defective items</li>
              <li>Incorrect items shipped</li>
              <li>Errors on our part</li>
            </ul>
            <p>
              In these cases, we will provide a prepaid return shipping label.
            </p>
          </div>

          <div className="returns-section">
            <h3>Exceptions</h3>
            <p>The following items are non-returnable:</p>
            <ul>
              <li>
                <strong>Custom or Personalized Items:</strong> Items that have
                been customized or personalized to your specifications.
              </li>
              <li>
                <strong>Digital Products:</strong> Downloadable software,
                eBooks, and digital content.
              </li>
              <li>
                <strong>Perishable Goods:</strong> Food items, plants, and other
                perishable products.
              </li>
              <li>
                <strong>Health and Hygiene Products:</strong> Cosmetics,
                personal care items, and intimate apparel that have been opened.
              </li>
              <li>
                <strong>Sale Items:</strong> Items purchased on clearance or
                marked as final sale.
              </li>
            </ul>
          </div>

          <div className="returns-section">
            <h3>Damaged or Defective Items</h3>
            <p>
              If you receive a damaged or defective item, please contact us
              immediately at{" "}
              {tenant?.BusinessEmail ? (
                <a href={`mailto:${tenant.BusinessEmail}`}>
                  {tenant.BusinessEmail}
                </a>
              ) : (
                "support@example.com"
              )}{" "}
              with photos of the damage. We will arrange for a replacement or
              full refund, including shipping costs.
            </p>
          </div>

          <div className="returns-section">
            <h3>Contact Us</h3>
            <p>
              If you have any questions about our returns policy, please contact
              us:
            </p>

            {/* Email - from backend */}
            {tenant?.BusinessEmail && (
              <p>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${tenant.BusinessEmail}`}>
                  {tenant.BusinessEmail}
                </a>
              </p>
            )}

            {/* Phone - from backend with proper formatting */}
            {tenant?.BusinessPhone && (
              <p>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${formatTelLink(tenant.BusinessPhone)}`}>
                  {tenant.BusinessPhone}
                </a>
              </p>
            )}

            {/* Address - from backend */}
            {(tenant?.AddressLine1 || tenant?.City) && (
              <p>
                <strong>Returns Address:</strong>{" "}
                {[
                  tenant?.AddressLine1,
                  tenant?.AddressLine2,
                  tenant?.City,
                  tenant?.State,
                  tenant?.PostalCode,
                  tenant?.Country,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            )}

            {/* Fallback contact info if no tenant data exists */}
            {!tenant?.BusinessEmail &&
              !tenant?.BusinessPhone &&
              !tenant?.AddressLine1 &&
              !tenant?.City && (
                <p>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:returns@example.com">returns@example.com</a>
                </p>
              )}
          </div>

          <div className="returns-section last-updated">
            <p className="last-updated-text">
              Last updated:{" "}
              {new Date().toLocaleDateString("en-ZA", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnsPolicy;
